/**
 * convertClip — Three AnimationClip → kernel ClipData (ADR-008 §2, Convert
 * stage). Takes the ROOT-MOTION DECLARATION as a required parameter: no
 * ClipData ever exists with an undeclared root channel — `null` here is a
 * deliberate "none", never a placeholder (Codex, PR #12). Every liberty
 * (drop, resample, inference, duration fix) lands in the ClipImportReport.
 * Depends on: three, @harryhausen/kernel. Depended on by: app/import.
 */
import {
  AnimationClip,
  InterpolateDiscrete,
  InterpolateLinear,
  KeyframeTrack,
  Matrix4,
  PropertyBinding,
  Quaternion,
  Vector3,
} from 'three';
import { parseClipData, type ClipData } from '@harryhausen/kernel';
import { InterposedFrameRotationError } from './errors.js';
import type {
  ClipImportReport,
  DroppedTrackEntry,
  FpsProvenance,
  ImportTransform,
  NormalizedTrackEntry,
  ResampledTrackEntry,
  TrackSpaceTransform,
} from './import-report.js';

export interface ConvertClipOptions {
  /**
   * The rig bone carrying root motion — decided by the Declare stage AFTER
   * mapping (the Hips-mapped bone), or a deliberate `null` = none. Required:
   * the adapter never guesses this (ADR-003 §2c, ADR-008 §2).
   */
  readonly rootMotionTarget: string | null;
  /** Declared import rate used when no uniform key grid is inferable (default 30). */
  readonly declaredFps?: number;
  /**
   * Ordered morph-target names per node name — GLTFLoader emits ONE
   * multi-value `.morphTargetInfluences` track per morphed mesh (never
   * indexed tracks), so splitting it into per-target ClipData tracks needs
   * the mesh's dictionary (Codex, PR #13). Build with collectMorphTargetNames.
   */
  readonly morphTargets?: ReadonlyMap<string, readonly string[]>;
  /**
   * Per-bone interposed transforms from convertRig — applied to matching
   * translation tracks so animated root motion shares worldBind space (the
   * Meshy Armature-scale seam, ADR-008 §2b). A rotating interposed frame is
   * refused loudly (InterposedFrameRotationError); absence = identity = no-op.
   */
  readonly trackNormalization?: readonly TrackSpaceTransform[];
}

/**
 * Fold an interposed transform into a translation track — the same transform
 * convertRig folded into the bone's localBind. Callers guarantee no rotation
 * (guarded upstream), so this is a scale+offset in practice; the full compose
 * keeps it exact if that ever changes.
 */
function normalizeTranslation(values: Float32Array, transform: ImportTransform): Float32Array {
  const m = new Matrix4().compose(
    new Vector3(transform.position.x, transform.position.y, transform.position.z),
    new Quaternion(transform.rotation.x, transform.rotation.y, transform.rotation.z, transform.rotation.w),
    new Vector3(transform.scale.x, transform.scale.y, transform.scale.z),
  );
  const out = new Float32Array(values.length);
  const v = new Vector3();
  for (let i = 0; i + 2 < values.length; i += 3) {
    v.set(values[i] as number, values[i + 1] as number, values[i + 2] as number).applyMatrix4(m);
    out[i] = v.x;
    out[i + 1] = v.y;
    out[i + 2] = v.z;
  }
  return out;
}

/** Ordered morph-target names per node, from the loaded scene's dictionaries. */
export function collectMorphTargetNames(scene: { traverse(cb: (o: unknown) => void): void }): Map<string, readonly string[]> {
  const map = new Map<string, readonly string[]>();
  scene.traverse((node) => {
    const mesh = node as { name?: string; morphTargetDictionary?: Record<string, number> };
    if (mesh.morphTargetDictionary && mesh.name) {
      const ordered: string[] = [];
      for (const [name, index] of Object.entries(mesh.morphTargetDictionary)) ordered[index] = name;
      map.set(mesh.name, ordered);
    }
  });
  return map;
}

export interface ConvertClipResult {
  readonly clip: ClipData;
  readonly report: ClipImportReport;
}

const GRID_RELATIVE_TOLERANCE = 1e-3;
const MIN_GRID_INTERVALS = 4;

/**
 * Detect a uniform key grid; null when there is none to infer. Only LINEAR
 * tracks vote — a dense uniform linear grid is the signature of a baked
 * export, which is what the inferred rate means. Sparse authored tracks
 * (splines, steps, 2-key poses) are trivially "uniform" without being
 * evidence of anything, so they are excluded and a minimum interval count
 * is required (ADR-008 §2f: the fallback is the DECLARED rate, loudly).
 */
function inferGridFps(tracks: readonly KeyframeTrack[]): number | null {
  const diffs: number[] = [];
  for (const t of tracks) {
    if (isCubicSpline(t) || t.getInterpolation() !== InterpolateLinear) continue;
    for (let i = 1; i < t.times.length; i += 1) {
      diffs.push((t.times[i] as number) - (t.times[i - 1] as number));
    }
  }
  if (diffs.length < MIN_GRID_INTERVALS) return null;
  const sorted = [...diffs].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] as number;
  if (median <= 0) return null;
  const uniform = diffs.every((d) => Math.abs(d - median) <= GRID_RELATIVE_TOLERANCE * median);
  if (!uniform) return null;
  // Snap to an integer rate within float32 grid noise (24.0000014 → 24);
  // non-integer broadcast rates like 29.97 stay as measured.
  const fps = 1 / median;
  return Math.abs(fps - Math.round(fps)) < 1e-2 ? Math.round(fps) : fps;
}

/**
 * createInterpolant exists on every KeyframeTrack at runtime but is absent
 * from @types/three 0.185 — a types gap, not an API liberty.
 */
interface InterpolantLike {
  readonly resultBuffer: { readonly length: number };
  evaluate(time: number): ArrayLike<number>;
}
interface TrackInterpolantAccess {
  createInterpolant: ((result?: unknown) => InterpolantLike) & {
    isInterpolantFactoryMethodGLTFCubicSpline?: boolean;
  };
}

/** GLTFLoader marks CUBICSPLINE tracks by flagging its interpolant factory. */
function isCubicSpline(track: KeyframeTrack): boolean {
  return (
    (track as unknown as TrackInterpolantAccess).createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline === true
  );
}

interface ParsedTarget {
  readonly nodeName: string;
  readonly propertyName: string;
  readonly propertyIndex: string | undefined;
}

function parseTarget(trackName: string): ParsedTarget {
  const parsed = PropertyBinding.parseTrackName(trackName) as {
    nodeName: string;
    propertyName: string;
    propertyIndex?: string;
  };
  return { nodeName: parsed.nodeName, propertyName: parsed.propertyName, propertyIndex: parsed.propertyIndex };
}

/**
 * Does the clip carry a translation track for this bone? The Declare stage's
 * evidence: root motion may only be declared on a bone that actually has a
 * translation channel (kernel schema, Codex PR #13) — the orchestrator
 * checks before declaring instead of catching the gate's rejection.
 */
export function hasTranslationTrack(threeClip: AnimationClip, boneName: string): boolean {
  return threeClip.tracks.some((t) => {
    const { nodeName, propertyName } = parseTarget(t.name);
    return nodeName === boneName && propertyName === 'position';
  });
}

export function convertClip(
  threeClip: AnimationClip,
  boneNames: ReadonlySet<string>,
  options: ConvertClipOptions,
): ConvertClipResult {
  // fps first — resampling density depends on it (ADR-008 §2f).
  const inferred = inferGridFps(threeClip.tracks);
  const fps = inferred ?? options.declaredFps ?? 30;
  const fpsProvenance: FpsProvenance =
    inferred !== null ? 'inferred:key-grid' : options.declaredFps !== undefined ? 'declared:explicit' : 'declared:default';

  const resampled: ResampledTrackEntry[] = [];
  const dropped: DroppedTrackEntry[] = [];
  const morphTracks: string[] = [];
  const normById = new Map((options.trackNormalization ?? []).map((n) => [n.bone, n]));
  const normalizedTranslation: NormalizedTrackEntry[] = [];
  const tracks: Array<{ target: string; path: 'rotation' | 'translation' | 'scale' | 'morph'; times: Float32Array; values: Float32Array }> = [];

  let maxTime = 0;
  for (const t of threeClip.tracks) maxTime = Math.max(maxTime, (t.times[t.times.length - 1] as number) ?? 0);
  let durationSec = threeClip.duration > 0 ? threeClip.duration : maxTime;
  let durationAdjusted: ClipImportReport['durationAdjusted'] = null;
  if (durationSec <= 0) {
    // A single-key pose clip has zero duration; ClipData requires positive.
    // One frame is declared, LOUDLY — never silently (ADR-008 §2h).
    durationSec = 1 / fps;
    durationAdjusted = { declaredDurationSec: durationSec };
  }

  interface Materialized {
    readonly times: Float32Array;
    readonly values: Float32Array;
    readonly valueSize: number;
    /** Present when the track was resampled; pushed to the report only on acceptance. */
    readonly resampleInfo: { interpolation: 'STEP' | 'CUBICSPLINE'; keysBefore: number; keysAfter: number } | null;
  }

  // Interpolation handling, SHARED by every path — morph tracks included
  // (Codex, PR #13). Returns null for interpolation modes we cannot honor.
  const materialize = (track: KeyframeTrack): Materialized | null => {
    const cubic = isCubicSpline(track);
    const step = !cubic && track.getInterpolation() === InterpolateDiscrete;
    if (cubic || step) {
      // Resample to linear at the clip fps via the track's OWN interpolant —
      // GLTFLoader's cubic factory evaluates the authored tangents (ADR-008 §2g).
      // ceil + explicit final clamp so the authored endpoint always survives
      // (round() lost it for off-grid durations — Codex, PR #13); a float32
      // collapse of the clamped tail is dropped to keep times strictly ascending.
      const interpolant = (track as unknown as TrackInterpolantAccess).createInterpolant();
      const valueSize = interpolant.resultBuffer.length;
      const sampleTimes: number[] = [];
      const gridCount = Math.max(1, Math.ceil(durationSec * fps - 1e-9)) + 1;
      for (let i = 0; i < gridCount; i += 1) sampleTimes.push(Math.min(i / fps, durationSec));
      let times = Float32Array.from(sampleTimes);
      const n = times.length;
      if (n >= 2 && (times[n - 1] as number) <= (times[n - 2] as number)) {
        times = times.slice(0, n - 1);
      }
      const values = new Float32Array(times.length * valueSize);
      for (let i = 0; i < times.length; i += 1) {
        const result = interpolant.evaluate(times[i] as number) as ArrayLike<number>;
        for (let k = 0; k < valueSize; k += 1) values[i * valueSize + k] = result[k] as number;
      }
      return {
        times,
        values,
        valueSize,
        resampleInfo: {
          interpolation: cubic ? 'CUBICSPLINE' : 'STEP',
          keysBefore: track.times.length,
          keysAfter: times.length,
        },
      };
    }
    if (track.getInterpolation() !== InterpolateLinear) return null;
    return {
      times: Float32Array.from(track.times),
      values: Float32Array.from(track.values),
      valueSize: track.times.length > 0 ? track.values.length / track.times.length : 0,
      resampleInfo: null,
    };
  };

  const accept = (track: KeyframeTrack, m: Materialized): void => {
    if (m.resampleInfo) resampled.push({ track: track.name, ...m.resampleInfo });
  };

  for (const track of threeClip.tracks) {
    const { nodeName, propertyName, propertyIndex } = parseTarget(track.name);

    if (propertyName === 'morphTargetInfluences') {
      const m = materialize(track);
      if (m === null) {
        dropped.push({ track: track.name, reason: `unsupported interpolation mode ${track.getInterpolation()}` });
        continue;
      }
      if (propertyIndex !== undefined) {
        // Hand-authored Three form: one track per named target.
        accept(track, m);
        morphTracks.push(track.name);
        tracks.push({ target: propertyIndex, path: 'morph', times: m.times, values: m.values });
        continue;
      }
      // GLTFLoader form: ONE multi-value track per morphed mesh — split it
      // into per-target ClipData tracks via the mesh's dictionary (Codex, PR #13).
      const names = options.morphTargets?.get(nodeName);
      if (names === undefined) {
        dropped.push({
          track: track.name,
          reason: `multi-value morph track with no morph-target dictionary for node "${nodeName}" — pass collectMorphTargetNames(scene)`,
        });
        continue;
      }
      if (names.length !== m.valueSize) {
        dropped.push({
          track: track.name,
          reason: `morph dictionary for "${nodeName}" names ${names.length} target(s) but the track carries ${m.valueSize} per key`,
        });
        continue;
      }
      accept(track, m);
      morphTracks.push(track.name);
      names.forEach((name, k) => {
        const values = new Float32Array(m.times.length);
        for (let i = 0; i < m.times.length; i += 1) values[i] = m.values[i * m.valueSize + k] as number;
        tracks.push({ target: name, path: 'morph', times: m.times, values });
      });
      continue;
    }

    if (propertyName !== 'quaternion' && propertyName !== 'position' && propertyName !== 'scale') {
      dropped.push({ track: track.name, reason: `unsupported property "${propertyName}"` });
      continue;
    }
    if (!boneNames.has(nodeName)) {
      dropped.push({ track: track.name, reason: 'target is not a rig bone' });
      continue;
    }
    const m = materialize(track);
    if (m === null) {
      dropped.push({ track: track.name, reason: `unsupported interpolation mode ${track.getInterpolation()}` });
      continue;
    }
    accept(track, m);

    // Fold the bone's interposed transform into its animated translation so it
    // shares worldBind space with the rig descriptor (ADR-008 §2b). Only bones
    // with a non-bone ancestor appear; a rotating interposed frame is refused.
    let values = m.values;
    if (propertyName === 'position') {
      const norm = normById.get(nodeName);
      if (norm !== undefined) {
        if (norm.hasRotation) throw new InterposedFrameRotationError(nodeName);
        values = normalizeTranslation(values, norm.transform);
        normalizedTranslation.push({ bone: nodeName, applied: norm.transform });
      }
    }
    tracks.push({
      target: nodeName,
      path: propertyName === 'quaternion' ? 'rotation' : propertyName === 'position' ? 'translation' : 'scale',
      times: m.times,
      values,
    });
  }

  // The kernel validator is the gate (ADR-008 §2, Validate stage).
  const clip = parseClipData({
    name: threeClip.name || 'untitled-clip',
    fps,
    durationSec,
    additive: false, // glTF clips are absolute pose tracks; additive layers are authored later
    rootMotionTarget: options.rootMotionTarget,
    tracks,
  });

  return {
    clip,
    report: {
      fps: { value: fps, provenance: fpsProvenance },
      resampled,
      dropped,
      morphTracks,
      durationAdjusted,
      normalizedTranslation,
    },
  };
}
